import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {TableColumnDef} from '../../../shared/tables/table-column-def';
import {Table} from 'primeng/table';
import {FiltersService} from '../../../core/services';
import {FilterMetadata} from 'primeng/api';
import {FilterUtils} from 'primeng/utils';
import {PrTreeNode, SelectTreeComponent} from '../select-tree/select-tree.component';
import {CategoriesTree} from '../../../core';
import {MatMenuTrigger} from '@angular/material/menu';
import {TreeTable} from "primeng/treetable";

@Component({
    selector: 'pr-select-by-column',
    templateUrl: './select-by-column.component.html',
    styleUrls: ['./select-by-column.component.scss']
})
export class SelectByColumnComponent implements OnInit {

    @ViewChild('prSelectedTree', {static: false}) prSelectedTree: SelectTreeComponent;
    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

    @Input()
    column: TableColumnDef;

    @Input()
    dt: Table;

    initialSelectedNodeTree: { id: number, label: string }[];

    filterList = [];
    filteredFilterList = [];
    filterChecked: { [name: string]: boolean } = {};

    searchByText = '';
    searchInList = '';
    searched = false;

    private searchCategories: PrTreeNode[];

    private filters: {
        [s: string]: FilterMetadata;
    };

    constructor(
        private filtersService: FiltersService
    ) {
    }

    ngOnInit(): void {
        this.filters = this.dt.filters;

        this.setList();

        FilterUtils['multipleSearch'] = (value: string, filter: string[]) => {
            let found = false;

            filter.forEach(tmpFilter => {
                if (value.toString().indexOf(tmpFilter) !== -1) {
                    found = true;
                }
            });

            return found;
        };

        FilterUtils['multipleCategoriesSearch'] = (value: string, filter: { id: number, label: string }[]) => {
            return filter.find((val) => value.indexOf(val.label) !== -1);
        };
    }

    selectedCategories(selectedCategories: PrTreeNode[]) {
        const parentSelected: number[] = selectedCategories.filter((category) => !category.leaf).map((parent) => parent.id);

        const parentLength: {[parentId: number]: number} = this.getParentChildrenLength(parentSelected);
        const skipParent = [];

        parentSelected.forEach((parentId) => {
            if (parentLength && parentLength[parentId]) {
                const childLength = selectedCategories.filter((category) => category.parentId === parentId).length;
                if (childLength < parentLength[parentId]) {
                    skipParent.push(parentId);
                }
            }
        });

        this.searchCategories = selectedCategories.filter((e: PrTreeNode) => !skipParent.includes(e.id));
    }

    clearSearchByColumn(col: TableColumnDef) {
        this.searchByText = '';
        this.dt.filter(
            '',
            col.field,
            'contains'
        );

        this.searched = false;
    }

    searchByColumn(col: TableColumnDef) {
        this.dt.filter(
            this.searchByText,
            col.field,
            'contains'
        );

        this.searched = !!(this.searchByText);
        this.trigger.closeMenu();
    }

    searchInListChanged() {
        this.filteredFilterList = this.filterList.filter((item) => {
            return item && item.toLowerCase().search(this.searchInList.toLowerCase()) !== -1;
        });
    }

    clearSearchInList(event) {
        Object.keys(this.filterChecked).forEach((key) => {
            this.filterChecked[key] = false;
        });

        this.searchInList = '';
        this.filteredFilterList = [].concat(this.filterList);

        this.dt.filter(
            '',
            this.column.field,
            'contains'
        );

        this.searched = false;

        if (this.column.filterValue === 'Categories') {
            this.prSelectedTree.clearSelected();
            this.prSelectedTree.clearSearch();
        }
    }

    searchByColumnList(event) {
        if (this.column.filterValue === 'Categories') {
            this.dt.filter(
                (this.searchCategories && this.searchCategories.length > 0) ? this.searchCategories.map((category) => this.getCategoryToSave(category, this.searchCategories)) : [],
                this.column.field,
                'multipleCategoriesSearch'
            );

            this.searched = (this.searchCategories && this.searchCategories.length > 0);
        } else {
            const values = [];

            Object.keys(this.filterChecked).forEach((key) => {
                if (this.filterChecked[key]) {
                    values.push(key);
                }
            });

            this.dt.filter(
                (values.length > 0) ? values : '',
                this.column.field,
                (values.length > 0) ? 'multipleSearch' : 'contains'
            );

            this.searched = values.length > 0;
        }
    }

    private setList() {
        if (this.column.filterValue) {
            if (this.filtersService.hasFiltersLoaded()) {
                this.loadList();
            } else {
                this.filtersService.$filters.subscribe(res => {
                    this.loadList();
                });
            }
        } else {

            if (this.filters[this.column.field]) {
                this.searchByText = this.filters[this.column.field].value;
                this.searched = true;
            }
        }
    }

    private loadList() {
        this.filterList = this.filtersService.getFiltersByName(this.column.filterValue) || [];
        this.filteredFilterList = [].concat(this.filterList);
        this.setCheckedList();

        if (this.column.filterValue === 'Categories' && this.filters[this.column.field]) {
            this.initialSelectedNodeTree = this.filters[this.column.field].value;
            this.searched = true;
        }
    }

    private setCheckedList() {
        this.filterList.forEach((filter) => {
            this.filterChecked[filter] = false;
        });

        if (this.filters[this.column.field]) {
            this.filters[this.column.field].value.forEach((val) => {
                this.filterChecked[val] = true;
            });
            this.searched = true;
        }
    }

    private getCategoryBreadcrumb(category: PrTreeNode, allSelected: PrTreeNode[]) {
        if (category.parentId) {
            const parent: PrTreeNode = category.parent || allSelected.filter((sel) => sel.id === category.parentId)[0];
            return this.getCategoryBreadcrumb(parent, allSelected) + '/' + category.label;
        } else {
            return category.label;
        }
    }

    private getCategoryToSave(category: PrTreeNode, allSelected: PrTreeNode[]): ICategorySelected {
        const categoryLabel = this.getCategoryBreadcrumb(category, allSelected);

        return {
            id: category.id,
            label: categoryLabel,
            parentId: category.parentId,
            leaf: category.leaf,
            level: category.level
        };
    }

    private getParentChildrenLength(parentIds: number[]) {
        const orgFilterData: CategoriesTree[] = this.filtersService.getFiltersByName('Categories');
        const parentChildrenLength: {[parentId: number]: number} = {};

        parentIds.forEach((parentId) => {
            parentChildrenLength[parentId] = this.findChildrenLength(parentId, orgFilterData);
        });

        return parentChildrenLength;
    }

    private findChildrenLength(parentId: number, data: CategoriesTree[]) {
        const foundParent = data.find((treeNode) => treeNode.id === parentId);

        if (foundParent) {
            return foundParent.children.length;
        } else {
            let childrenLength = -1;

            for (let i = 0, len = data.length; i < len; i++) {
                const treeNode = data[i];

                if (treeNode.children && treeNode.children.length > 0) {
                    if (!treeNode.children[0].leaf) {
                        const res = this.findChildrenLength(parentId, treeNode.children);
                        if (res) {
                            childrenLength = res;
                            break;
                        }
                    }
                }
            }

            return childrenLength;
        }
    }
}

export interface ICategorySelected {
    id: number;
    label: string;
    parentId: number;
    leaf: boolean;
    level: number;
}
